import React from 'react';
import { cn } from '@utils/cn';
import { BudgetRowStatus, BUDGET_ROW_STATUS_LABEL } from '@models/budget.model';

interface BidStatusBadgeProps {
  status: BudgetRowStatus;
  size?: 'sm' | 'md';
  className?: string;
}

const sizeClasses = {
  sm: 'px-2 py-0.5 text-[11px] h-5',
  md: 'px-3 py-1 text-xs h-6',
};

/**
 * Hue carries meaning, not decoration: amber is "you are holding this up", blue
 * is "a conversation is live", green and red are settled outcomes, grey is over.
 * Only the two amber states should pull the eye across a full page of rows.
 */
const colorByStatus: Record<BudgetRowStatus, string> = {
  NEW: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  COUNTERED: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  NEGOTIATING: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200',
  RESPONDED: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  AWAITING: 'bg-slate-100 text-slate-700 dark:bg-slate-700/50 dark:text-slate-200',
  VIEWED: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200',
  ACCEPTED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  REJECTED: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  CANCELLED: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  CLOSED: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

export const BidStatusBadge: React.FC<BidStatusBadgeProps> = ({
  status,
  size = 'sm',
  className,
}) => (
  <span
    className={cn(
      'inline-flex w-fit items-center justify-center whitespace-nowrap rounded-full font-semibold uppercase tracking-wide',
      sizeClasses[size],
      colorByStatus[status] ?? colorByStatus.CLOSED,
      className,
    )}>
    {BUDGET_ROW_STATUS_LABEL[status] ?? status}
  </span>
);

export default BidStatusBadge;
